import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { MaterialModule } from 'src/app/material.module';
import { IMiSimpleTableColumn, MiTableSimpleComponent } from 'src/app/shared/components/mi-table-simple/mi-table-simple.component';
import { StackDirectives } from 'src/app/shared/directives/stack-directives';
import { IListBox } from 'src/app/shared/interfaces/IClaveValor';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { PersonWorkingService } from '../../services/person-working.service';
import { ModalInfoLaboral } from '../modal-info-laboral/modal-info-laboral';
import { MiAdmPersonWorking } from '../../interfaces/IAdmPersonWorking';
import { FormBuilder, Validators } from '@angular/forms';
import { ConfirmDeleteDialogComponent } from 'src/app/shared/components/confirm-delete/confirm-delete.component';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map, shareReplay, Subscription } from 'rxjs';

@Component({
  selector: 'app-info-laboral',
  imports: [StackDirectives, MaterialModule, CommonModule, MiTableSimpleComponent],
  templateUrl: './info-laboral.html',
  styleUrl: './info-laboral.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InfoLaboral {
  private loader = inject(LoaderService);
  private toastr = inject(ToastrService);
  private personWorkingService = inject(PersonWorkingService);
  private ac = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  private fb = inject(FormBuilder);
  private bp = inject(BreakpointObserver);
  private sub = new Subscription();


  idEncrypt: any;
  monthsList: IListBox[];
  indInexp: boolean = false;

  @Input() codEmp!: string;
  @Input() tipIde!: string;

  isMobile = signal<boolean>(false);

  @Output() validChange = new EventEmitter<boolean>();

  readonly dataSource = signal<any[]>([]);
  readonly columns = signal<IMiSimpleTableColumn[]>([]);
  readonly displayedColumns = signal<string[]>([]);
  readonly filterColumns = signal<string[]>([]);

  form = this.fb.group({
    mesIni: [null],
    anoIni: [null],
    mesFin: [null],
    anoFin: [null],
    actLab: [false, [Validators.required]],
    empresa: ['', [Validators.required]],
    cargo: ['', [Validators.required, Validators.maxLength(100)]],
    funciones: ['', [Validators.required, Validators.maxLength(100)]]
  });

  ngOnInit() {
    this.ac.queryParams.subscribe((params) => {
      const rawId = params['q'] as any;
      this.idEncrypt = encodeURIComponent(rawId);
      if (this.idEncrypt) {
        this.monthsList = this.generateMonthsList();
        this.getDataWorking(this.idEncrypt);
        
        const s = this.bp
          .observe(['(max-width: 768px)']) // ajusta el umbral a tu diseño
          .pipe(
            map((r) => r.matches),
            shareReplay({ bufferSize: 1, refCount: true })
          )
          .subscribe((isSmall) => this.isMobile.set(isSmall));

        this.sub.add(s);
      }
    });
  }

  getDataWorking(id: string) {
    this.loader.show('Cargando experiencia laboral...');
    this.personWorkingService.getById(id).subscribe({
      next: (response) => {
        // console.log('Datos experiencia laboral recibidos:', response);
        this.loader.hide();

        // Emitir evento de validación
        if (response.data.length === 0) {
          this.validChange.emit(false);
        } else {
          this.validChange.emit(true);
        }

        this.indInexp = this.validateInexperiencia(response.data);
        const dataComplete = response.data.map((item) => {
          const nomMesIni =
            this.monthsList.length > 0 && item.mesInicio != null
              ? this.monthsList.find((c: any) => c.id === item.mesInicio?.toString())?.label + ' ' + item.anioInicio
              : '';
          const nomMesFin =
            this.monthsList.length > 0 && item.mesFin != null
              ? this.monthsList.find((c: any) => c.id === item.mesFin?.toString())?.label + ' ' + item.anioFin
              : '';
          const cargoFunciones = `<strong>${item.cargo}</strong><br>${item.funciones}`;

          let periodo = '';
          const iniVacio = nomMesIni.trim().length === 0;
          const finVacio = nomMesFin.trim().length === 0;

          if (iniVacio && finVacio) {
            periodo = ''; // no mostrar nada
          } else if (!iniVacio && finVacio) {
            periodo = `${nomMesIni} - Actualmente estudiando`;
          } else {
            // Ambos presentes: muestra rango
            periodo = `${nomMesIni} - ${nomMesFin}`; // en dash para mejor tipografía
          }

          return {
            ...item,
            cargoFunciones: cargoFunciones,
            periodo: periodo
          };
        });

        this.dataSource.set(dataComplete);
        this.displayedColumns.set(['empresa', 'cargoFunciones', 'periodo']);
        this.filterColumns.set(['empresa', 'cargoFunciones', 'periodo']);
        this.columns.set([
          { field: 'empresa', header: 'Empresa', sortable: true, bold: true, align: 'left' },
          { field: 'cargoFunciones', header: 'Cargo y Funciones', sortable: true, bold: true, type: 'area' },
          { field: 'periodo', header: 'Periodo', sortable: true, align: 'left' }
        ]);
      },
      error: (error) => {
        this.loader.hide();
        this.toastr.error('Error al cargar', error.message);
      }
    });
  }

  generateMonthsList(): IListBox[] {
    const months = [
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre'
    ];

    return months.map((month, index) => ({
      label: month,
      id: (index + 1).toString()
    }));
  }

  openModalCreate() {
    this.dialog
      .open(ModalInfoLaboral, {
        width: '50%',
        maxWidth: '100vw',
        data: {
          idEncrypt: this.idEncrypt,
          codEmp: this.codEmp,
          tipIde: this.tipIde
        }
      })
      .afterClosed()
      .subscribe((response) => {
        this.getDataWorking(this.idEncrypt);
      });
  }

  onEdit(row: MiAdmPersonWorking) {
    this.dialog
      .open(ModalInfoLaboral, {
        width: '50%',
        maxWidth: '100vw',
        data: {
          idEncrypt: this.idEncrypt,
          codEmp: this.codEmp,
          tipIde: this.tipIde,
          workingData: row
        }
      })
      .afterClosed()
      .subscribe((response) => {
        this.getDataWorking(this.idEncrypt);
      });
  }

  onDelete(row: any) {
    this.dialog
      .open(ConfirmDeleteDialogComponent, {
        width: '400px',
        data: {
          title: 'Confirmar eliminación',
          message: '¿Está seguro de que desea eliminar este registro?'
        }
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.loader.show('Eliminando registro...');
          this.personWorkingService.delete(this.idEncrypt, row.id.toString()).subscribe({
            next: (response) => {
              this.loader.hide();
              this.toastr.success('Registro eliminado correctamente', 'Éxito');
              this.getDataWorking(this.idEncrypt);
            },
            error: (error) => {
              this.loader.hide();
              this.toastr.error('Error al eliminar');
            }
          });
        }
      });
  }

  createDataWorking() {
    if (this.form.valid) {
      this.loader.show('Guardando información laboral...');
      const payload = this.setPersonWorkingData();
      // console.log('Payload a enviar:', payload);
      this.personWorkingService.createFull(this.idEncrypt, payload).subscribe({
        next: (response) => {
          this.loader.hide();
          this.toastr.success('Información laboral guardada con éxito');
          this.getDataWorking(this.idEncrypt);
        },
        error: (err) => {
          this.loader.hide();
          this.toastr.error('Error al guardar la información laboral');
        }
      });
    } else {
      this.form.markAllAsTouched(); // Para mostrar errores
    }
  }

  setPersonWorkingData(): MiAdmPersonWorking {
    const isWorking = this.form.value.actLab;
    return {
      id: 0,
      idTipoIdentificacion: Number.parseInt(this.tipIde),
      identificacion: this.codEmp,
      mesInicio: Number.parseInt(this.form.value.mesIni!),
      anioInicio: Number.parseInt(this.form.value.anoIni!),
      mesFin: isWorking ? null : Number.parseInt(this.form.value.mesFin!),
      anioFin: isWorking ? null : Number.parseInt(this.form.value.anoFin!),
      actualmenteLaborando: this.form.value.actLab!,
      empresa: this.form.value.empresa!,
      cargo: this.form.value.cargo!,
      funciones: this.form.value.funciones!,
      idEstado: 1
    };
  }

  sinExperiencia() {
    this.form.patchValue({
      empresa: 'Sin experiencia',
      cargo: 'Sin experiencia',
      funciones: 'Sin experiencia',
      mesIni: null,
      anoIni: null,
      mesFin: null,
      anoFin: null,
      actLab: false
    });
    this.createDataWorking();
  }

  private validateInexperiencia(data: MiAdmPersonWorking[]): boolean {
    if (data.length === 0) return false;
    return data.some(
      (item) => (item.empresa && item.empresa.includes('Sin experiencia')) || (item.cargo && item.cargo.includes('Sin experiencia'))
    );
  }
}
